import React from 'react';

import { init as initElasticAPM } from '@elastic/apm-rum';
import * as Sentry from '@sentry/nextjs';
import App from 'next/app';
import dynamic from 'next/dynamic';
import { NuqsAdapter } from 'nuqs/adapters/next/pages';
import { Cookies, CookiesProvider } from 'react-cookie';

import { appWithTranslation } from '@i18n';

import { isAccountsPagePath, isNotAuthenticatedPagePath } from '@framework/accounts.utils';
import { loadRuntimeConfig } from '@framework/config/runtime';
import authenticationGuards, {
    fetchInitialAuthenticationStatus
} from '@framework/guards/authenticationGuards';
import routeGuard from '@framework/guards/routeGuard';
import ServerContext from '@framework/server/middleware';
import languageMiddleware from '@framework/server/middleware/language.middleware';
import parameterMiddleware from '@framework/server/middleware/parameter.middleware';
import { isWindowDefined } from '@framework/utils';
import { getNonSensitiveCookies } from '@framework/utils/cookies';

import StyledSystemContainer from '@components/misc/StyledSystemContainer/StyledSystemContainer';
import TranslationReadyGuard from '@components/misc/TranslationReadyGuard/TranslationReadyGuard';
import RuntimeProvider from '@components/pages/other/RuntimeContext/RuntimeProvider';
import ErrorFallback from '@components/ui/ErrorFallback/ErrorFallback';

import './_app.scss';

import nextI18nConfig from '../next-i18next.config';

const DynamicAuthenticatedApp = dynamic(() => import('./AuthenticatedApp'));
const DynamicUnauthenticatedApp = dynamic(() => import('./UnauthenticatedApp'));
const DynamicLoginApp = dynamic(() => import('./LoginApp'));
const DynamicNoInternetBar = dynamic(
    () => import('@components/layout/NoInternetBar/NoInternetBar'),
    { ssr: false }
);

let elasticAPMInitialized = false;

const initializeElasticAPM = (runtimeConfig) => {
    if (elasticAPMInitialized || !isWindowDefined()) return;

    initElasticAPM({
        serviceName: 'web',
        serviceVersion: runtimeConfig.VERSION,
        environment: runtimeConfig.SENTRY_ENVIRONMENT,
        serverUrl: runtimeConfig.ELASTIC_APM_URL,
        active: runtimeConfig.ELASTIC_APM_ENABLED
    });

    elasticAPMInitialized = true;
};

/**
 *
 *
 */
class MyApp extends App {
    static async getInitialProps(appContext) {
        const { ctx } = appContext;
        const isAccountsPage = isAccountsPagePath(ctx.pathname);
        const isNotAuthenticatedPage = isNotAuthenticatedPagePath(ctx.pathname);
        let cookies = {};
        let runtimeConfig = {};

        // check authentication status and redirect if necessary
        if (!isWindowDefined()) {
            const serverContext = new ServerContext(ctx);

            await serverContext.call(fetchInitialAuthenticationStatus);
            await serverContext.call(languageMiddleware);
            await serverContext.call(parameterMiddleware);
            await serverContext.call(authenticationGuards);

            if (!isAccountsPage && !isNotAuthenticatedPage) {
                await serverContext.call(routeGuard);
            }

            cookies = new Cookies(getNonSensitiveCookies(ctx.req?.headers.cookie));
        }

        try {
            const headers = ctx.req?.headers || {};
            runtimeConfig = await loadRuntimeConfig(headers);
        } catch (error) {
            runtimeConfig = {};
            Sentry.captureException(error, {
                level: 'fatal'
            });
        }

        const appProps = (await App.getInitialProps(appContext)) || {};

        return { ...appProps, cookies, isAccountsPage, isNotAuthenticatedPage, runtimeConfig };
    }

    componentDidCatch(error, errorInfo) {
        Sentry.withScope((scope) => {
            scope.setExtras(errorInfo);
            Sentry.captureException(error);
        });
    }

    componentDidMount() {
        const { runtimeConfig } = this.props;
        initializeElasticAPM(runtimeConfig);
    }

    render() {
        /**
         * Computation of `isAccountsPage` is only done on first page load, both server side and
         * client side. This means that client side routing won't update this value and more
         * work needs to be done to support it. For now, for our current case, this will work just fine.
         */
        const { cookies, isAccountsPage, isNotAuthenticatedPage, runtimeConfig } = this.props;

        const cookiesProps = isWindowDefined() ? {} : { cookies };

        return (
            <Sentry.ErrorBoundary
                fallback={({ eventId, resetError }) => (
                    <ErrorFallback eventId={eventId} resetError={resetError} />
                )}
            >
                <RuntimeProvider runtimeConfig={runtimeConfig}>
                    <NuqsAdapter>
                        <CookiesProvider {...cookiesProps}>
                            <StyledSystemContainer>
                                <TranslationReadyGuard>
                                    {isNotAuthenticatedPage && (
                                        <DynamicUnauthenticatedApp {...this.props} />
                                    )}
                                    {!isNotAuthenticatedPage && (
                                        <>
                                            {isAccountsPage && <DynamicLoginApp {...this.props} />}
                                            {!isAccountsPage && (
                                                <DynamicAuthenticatedApp {...this.props} />
                                            )}
                                        </>
                                    )}
                                    <DynamicNoInternetBar />
                                </TranslationReadyGuard>
                            </StyledSystemContainer>
                        </CookiesProvider>
                    </NuqsAdapter>
                </RuntimeProvider>
            </Sentry.ErrorBoundary>
        );
    }
}

export default appWithTranslation(MyApp, nextI18nConfig);
